import React, { useState, useRef, useEffect } from 'react';
import ReactDOM from 'react-dom';

const MONTHS = ['January','February','March','April','May','June','July','August','September','October','November','December'];
const DAYS   = ['Su','Mo','Tu','We','Th','Fr','Sa'];

const CalendarPicker = ({ name, value, onChange, required, placeholder }) => {
  const today = new Date();

  const parseVal = (v) => {
    if (!v) return null;
    const p = v.split('-').map(Number);
    if (p.length !== 3 || isNaN(p[0])) return null;
    return { y: p[0], m: p[1] - 1, d: p[2] };
  };

  const sel = parseVal(value);
  const [open, setOpen]           = useState(false);
  const [viewYear, setViewYear]   = useState(sel?.y || today.getFullYear());
  const [viewMonth, setViewMonth] = useState(sel?.m ?? today.getMonth());
  const [dropStyle, setDropStyle] = useState({});
  const ref = useRef(null);
  const dropRef = useRef(null);
  const DROP_WIDTH = 300;
  const DROP_HEIGHT = 380; // conservative estimate — enough for a 6-row month + header/footer

  const daysInMonth = new Date(viewYear, viewMonth + 1, 0).getDate();
  const firstDay    = new Date(viewYear, viewMonth, 1).getDay();

  const displayValue = sel
    ? `${String(sel.d).padStart(2,'0')}/${String(sel.m+1).padStart(2,'0')}/${sel.y}`
    : '';

  const openPicker = () => {
    const s = parseVal(value);
    setViewYear(s?.y || today.getFullYear());
    setViewMonth(s?.m ?? today.getMonth());
    // Position via fixed viewport coords so the popup can never push the
    // form/modal into horizontal scroll — a right-column field's absolute-
    // positioned popup would otherwise overflow past the container's edge.
    // Clamp horizontally against the modal card itself (not just the
    // viewport) — the viewport usually has slack the narrower modal
    // doesn't, so a viewport-only clamp still lets the popup poke out past
    // the card.
    const margin = 8;
    const r = ref.current?.getBoundingClientRect();
    if (r) {
      const boundsEl = ref.current.closest('.modal-content');
      const bounds = boundsEl ? boundsEl.getBoundingClientRect() : { left: 0, right: window.innerWidth };
      const minLeft = bounds.left + margin;
      const maxLeft = bounds.right - DROP_WIDTH - margin;
      let left = r.left;
      if (left > maxLeft) left = maxLeft;
      left = Math.max(minLeft, left);

      // Vertically, a fixed-position popup can't be reached by scrolling —
      // if it doesn't fit below the field within the browser window, flip
      // it above instead of letting it run off the bottom of the screen.
      const spaceBelow = window.innerHeight - r.bottom;
      const spaceAbove = r.top;
      let top;
      if (spaceBelow >= DROP_HEIGHT + margin || spaceBelow >= spaceAbove) {
        top = Math.min(r.bottom + 4, window.innerHeight - DROP_HEIGHT - margin);
      } else {
        top = r.top - DROP_HEIGHT - 4;
      }
      top = Math.max(margin, top);

      setDropStyle({ position: 'fixed', top, left, width: DROP_WIDTH });
    }
    setOpen(true);
  };

  useEffect(() => {
    if (!open) return;
    const close = (e) => {
      const inTrigger = ref.current && ref.current.contains(e.target);
      const inDropdown = dropRef.current && dropRef.current.contains(e.target);
      if (!inTrigger && !inDropdown) setOpen(false);
    };
    document.addEventListener('mousedown', close);
    document.addEventListener('touchstart', close);
    return () => {
      document.removeEventListener('mousedown', close);
      document.removeEventListener('touchstart', close);
    };
  }, [open]);

  const pick = (day) => {
    const m = String(viewMonth + 1).padStart(2, '0');
    const d = String(day).padStart(2, '0');
    onChange({ target: { name, value: `${viewYear}-${m}-${d}` } });
    setOpen(false);
  };

  const clear = () => {
    onChange({ target: { name, value: '' } });
    setOpen(false);
  };

  const goToday = () => {
    setViewYear(today.getFullYear());
    setViewMonth(today.getMonth());
    const m = String(today.getMonth() + 1).padStart(2, '0');
    const d = String(today.getDate()).padStart(2, '0');
    onChange({ target: { name, value: `${today.getFullYear()}-${m}-${d}` } });
    setOpen(false);
  };

  const prevMonth = () => {
    if (viewMonth === 0) { setViewMonth(11); setViewYear(y => y - 1); }
    else setViewMonth(m => m - 1);
  };
  const nextMonth = () => {
    if (viewMonth === 11) { setViewMonth(0); setViewYear(y => y + 1); }
    else setViewMonth(m => m + 1);
  };

  // Days from previous month to fill the first row
  const prevMonthDays = new Date(viewYear, viewMonth, 0).getDate();

  return (
    <div ref={ref} style={{ position: 'relative' }}>
      {required && (
        <input
          type="text"
          required
          value={value || ''}
          onChange={() => {}}
          tabIndex={-1}
          style={{ position: 'absolute', bottom: 0, left: 0, width: '100%', height: 0, opacity: 0, pointerEvents: 'none' }}
        />
      )}
      {/* Trigger field */}
      <div
        onClick={openPicker}
        className="form-input"
        style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          cursor: 'pointer', userSelect: 'none', minHeight: 40,
        }}
      >
        <span style={{ color: displayValue ? 'var(--text-1)' : '#9ca3af', fontSize: 13 }}>
          {displayValue || (placeholder || 'mm/dd/yyyy')}
        </span>
        <svg width="16" height="16" viewBox="0 0 20 20" fill="currentColor" style={{ flexShrink: 0, color: '#9ca3af' }}>
          <path fillRule="evenodd" d="M6 2a1 1 0 00-1 1v1H4a2 2 0 00-2 2v10a2 2 0 002 2h12a2 2 0 002-2V6a2 2 0 00-2-2h-1V3a1 1 0 10-2 0v1H7V3a1 1 0 00-1-1zm0 5a1 1 0 000 2h8a1 1 0 100-2H6z" clipRule="evenodd" />
        </svg>
      </div>

      {/* Calendar dropdown — portaled to <body> so it's positioned via fixed
          viewport coords, escaping the modal's clipping/transform context
          entirely (same technique as AutocompleteInput's dropdown). */}
      {open && ReactDOM.createPortal(
        <div ref={dropRef} style={{
          ...dropStyle, zIndex: 99999,
          background: '#fff', border: '1.5px solid #e0e4ef', borderRadius: 12,
          boxShadow: '0 8px 32px rgba(10,14,26,0.18)',
          padding: '14px 12px 10px', fontFamily: "'Plus Jakarta Sans', sans-serif",
        }}>
          {/* Month / Year header */}
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10 }}>
            <span style={{ fontWeight: 700, fontSize: 14, color: '#0d1117' }}>
              {MONTHS[viewMonth]} {viewYear}
            </span>
            <div style={{ display: 'flex', gap: 4 }}>
              <button type="button" onClick={prevMonth} style={{ width: 28, height: 28, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>↑</button>
              <button type="button" onClick={nextMonth} style={{ width: 28, height: 28, border: '1px solid #e5e7eb', background: '#fff', borderRadius: 6, cursor: 'pointer', fontSize: 14, display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#374151' }}>↓</button>
            </div>
          </div>

          {/* Day headers */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', marginBottom: 4 }}>
            {DAYS.map(d => (
              <div key={d} style={{ textAlign: 'center', fontSize: 11, color: '#9ca3af', fontWeight: 600, padding: '3px 0' }}>{d}</div>
            ))}
          </div>

          {/* Calendar grid */}
          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)' }}>
            {/* Leading days from prev month */}
            {Array.from({ length: firstDay }).map((_, i) => (
              <div key={`p${i}`} style={{ textAlign: 'center', padding: '6px 0', fontSize: 13, color: '#d1d5db' }}>
                {prevMonthDays - firstDay + i + 1}
              </div>
            ))}

            {/* Current month days */}
            {Array.from({ length: daysInMonth }, (_, i) => i + 1).map(day => {
              const isSel  = sel && sel.d === day && sel.m === viewMonth && sel.y === viewYear;
              const isToday = day === today.getDate() && viewMonth === today.getMonth() && viewYear === today.getFullYear();
              return (
                <div
                  key={day}
                  onClick={() => pick(day)}
                  style={{
                    textAlign: 'center', padding: '6px 0', fontSize: 13, cursor: 'pointer',
                    borderRadius: 4,
                    background: isSel ? '#0077b6' : 'transparent',
                    color: isSel ? '#fff' : isToday ? '#0077b6' : '#0d1117',
                    fontWeight: isSel || isToday ? 700 : 400,
                    outline: isToday && !isSel ? '2px solid #0077b640' : 'none',
                  }}
                >
                  {day}
                </div>
              );
            })}

            {/* Trailing days from next month */}
            {(() => {
              const total = firstDay + daysInMonth;
              const trailing = total % 7 === 0 ? 0 : 7 - (total % 7);
              return Array.from({ length: trailing }).map((_, i) => (
                <div key={`n${i}`} style={{ textAlign: 'center', padding: '6px 0', fontSize: 13, color: '#d1d5db' }}>
                  {i + 1}
                </div>
              ));
            })()}
          </div>

          {/* Bottom actions */}
          <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 8, paddingTop: 8, borderTop: '1px solid #f3f4f6' }}>
            <button type="button" onClick={clear}   style={{ background: 'none', border: 'none', color: '#0077b6', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>Clear</button>
            <button type="button" onClick={goToday} style={{ background: 'none', border: 'none', color: '#0077b6', fontSize: 13, fontWeight: 600, cursor: 'pointer', padding: '4px 0' }}>Today</button>
          </div>
        </div>,
        document.body
      )}
    </div>
  );
};

export default CalendarPicker;
